import { useEffect, useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  PinInput,
  PinInputField,
  Stack,
  Text,
  Tooltip,
  useBoolean,
  VStack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  getError,
  InfoCircleOutline,
  useToast,
  ArrowLeftOutline,
  EyeOutline,
  EyeSlashOutline,
} from '@aslanpay/portal-common-lib';

import PasswordInfo from 'app/components/UI/PasswordInfo';

import useForgotPasswordSubmit from 'app/hooks/useForgotPasswordSubmit';
import useResetIndividualPassword from 'app/hooks/useResetIndividualPassword';

import { useAuthenticationStore } from 'app/store';

import { authenticationSchema } from 'app/utils/schemas';

import { AuthenticationStepProps, ResetPasswordFields } from '../types';

function ResetPassword({ onSetStep }: AuthenticationStepProps) {
  const toast = useToast();

  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const resetAuthenticationData = useAuthenticationStore((state) => state.resetAuthenticationData);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.resetPassword),
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [resendMessage, setResendMessage] = useState('Code not received?');
  const [isResendDisabled, setIsResendDisabled] = useBoolean();

  const { mutate: resetIndividualPassword, isLoading: isResettingIndividualPassword } =
    useResetIndividualPassword({
      onError: (error: Error) => {
        const baseError = getError(error);

        if (baseError.status === 429) {
          setError('code', {
            message: 'Password reset limit exceeded, try again in a few minutes',
          });
        } else {
          setError('code', {
            message: 'Unable to resend verification code. Please try again later',
          });
        }
      },
    });

  const { mutate: submitForgotPassword, isLoading: isSubmittingForgotPassword } =
    useForgotPasswordSubmit({
      onSuccess: () => {
        resetAuthenticationData();

        toast({
          position: 'top-right',
          status: 'success',
          title: 'Passcode successfully reset',
          description: 'Please sign in using your new passcode.',
        });
        onSetStep('checkEmail');
      },
      onError: (error: Error) => {
        switch (error.name) {
          case 'CodeMismatchException':
            setError('code', { message: 'Wrong code. Please try again' });
            break;
          case 'ExpiredCodeException':
            setError('code', { message: 'Verification code already expired' });
            break;
          default:
            toast({
              position: 'top-right',
              status: 'error',
              title: 'Something went wrong',
              description: error.message,
            });
            break;
        }
      },
    });

  const handleSubmitForgotPassword = (data: ResetPasswordFields) => {
    submitForgotPassword({
      code: data.code,
      email: authenticationData.email,
      password: data.password,
    });
  };

  const handleResendCode = () => {
    resetIndividualPassword({
      email: authenticationData.email,
    });

    setIsResendDisabled.on();
    setResendMessage('A new verification code was sent!');
    setTimeout(() => {
      setResendMessage('Code not received?');
      setIsResendDisabled.off();
    }, 120000);
  };

  useEffect(() => {
    resetIndividualPassword({
      email: authenticationData.email,
    });
  }, [authenticationData.email, resetIndividualPassword]);

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => onSetStep('login')}
      >
        Back to login
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px">
        Reset password
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        We&apos;ve sent a verification code to:{' '}
        <Text as="span" color="brand.darkPurple">
          {authenticationData.email}
        </Text>
      </Text>

      <form onSubmit={handleSubmit(handleSubmitForgotPassword)} noValidate>
        <Stack spacing="35px">
          <FormControl isInvalid={!!errors?.code?.message} isRequired>
            <FormLabel>Verification code</FormLabel>
            <Controller
              control={control}
              name="code"
              render={({ field: { ref, onBlur: _onBlur, ...restField } }) => (
                <HStack spacing="20px" justify="center">
                  <PinInput
                    placeholder=""
                    size="lg"
                    {...restField}
                    isInvalid={!!errors?.code?.message}
                  >
                    <PinInputField ref={ref} />
                    <PinInputField />
                    <PinInputField />
                    <PinInputField />
                    <PinInputField />
                    <PinInputField />
                  </PinInput>
                </HStack>
              )}
            />
            <FormErrorMessage justifyContent="center">{errors?.code?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.password?.message} isRequired>
            <Flex align="flex-start" gap="-6px">
              <FormLabel mr="6px">Password</FormLabel>
              <Tooltip
                hasArrow
                arrowPadding={0}
                padding={0}
                background="brand.lightGray2"
                placement="top"
                label={<PasswordInfo />}
              >
                <Flex mt="1px">
                  <InfoCircleOutline boxSize="15px" color="brand.darkPurple" cursor="pointer" />
                </Flex>
              </Tooltip>
            </Flex>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your new password"
                {...register('password')}
              />
              <InputRightElement
                w="56px"
                h="full"
                cursor="pointer"
                onClick={() => setShowPassword((show) => !show)}
              >
                {showPassword ? (
                  <EyeOutline color="brand.mutedPurple" boxSize="24px" />
                ) : (
                  <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
                )}
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.confirmPassword?.message} isRequired>
            <FormLabel>Confirm password</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                {...register('confirmPassword')}
              />
              <InputRightElement
                w="56px"
                h="full"
                cursor="pointer"
                onClick={() => setShowConfirmPassword((show) => !show)}
              >
                {showConfirmPassword ? (
                  <EyeOutline color="brand.mutedPurple" boxSize="24px" />
                ) : (
                  <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
                )}
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors?.confirmPassword?.message}</FormErrorMessage>
          </FormControl>
        </Stack>
        <Text mt="42px" mb="22px" color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>
        <Button
          w="full"
          size="lg"
          type="submit"
          isLoading={isSubmittingForgotPassword}
          isDisabled={!isValid}
        >
          Reset
        </Button>
      </form>
      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          {resendMessage}
        </Text>
        <Button
          size="sm"
          variant="link"
          onClick={handleResendCode}
          isDisabled={isResendDisabled}
          isLoading={isResettingIndividualPassword}
        >
          Resend code
        </Button>
      </VStack>
      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          Having issues?
        </Text>
        <Link
          href="mailto:support@aslan.io"
          target="_blank"
          color="brand.purple"
          textStyle="smallMedium"
          textDecoration="underline"
        >
          Please contact support
        </Link>
      </VStack>
    </Flex>
  );
}

export default ResetPassword;
